import { useState, useEffect, useRef } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { 
  FileText, 
  PenTool, 
  Type, 
  Check, 
  AlertCircle, 
  Loader2,
  Download,
  Clock,
  User
} from "lucide-react";

import { API_URL } from "@/utils/api";

export default function SignDocumentPage() {
  const { sigRequestId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [sigRequest, setSigRequest] = useState(null);
  const [error, setError] = useState(null);
  
  // Signature state
  const [signatureType, setSignatureType] = useState("typed");
  const [typedSignature, setTypedSignature] = useState("");
  const [signerName, setSignerName] = useState("");
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  useEffect(() => {
    fetchSignatureRequest();
  }, [sigRequestId, token]);

  const fetchSignatureRequest = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/signature-requests/${sigRequestId}?token=${token}`
      );
      
      if (response.ok) {
        const data = await response.json();
        setSigRequest(data);
        setSignerName(data.recipient_name || "");
      } else {
        const errorData = await response.json();
        setError(errorData.detail || "Failed to load signature request");
      }
    } catch (err) {
      setError("Failed to connect to server");
    } finally {
      setLoading(false);
    }
  };

  // Canvas drawing functions
  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    const ctx = canvas.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    const ctx = canvas.getContext("2d");
    ctx.lineTo(x, y);
    ctx.strokeStyle = "#121212";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.stroke();
    setHasDrawn(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
  };

  const handleSubmit = async () => {
    if (!signerName.trim()) {
      toast.error("Please enter your name");
      return;
    }

    if (signatureType === "typed" && !typedSignature.trim()) {
      toast.error("Please type your signature");
      return;
    }

    if (signatureType === "drawn" && !hasDrawn) {
      toast.error("Please draw your signature");
      return;
    }

    setSubmitting(true);

    try {
      let drawnSignatureData = null;
      if (signatureType === "drawn" && canvasRef.current) {
        drawnSignatureData = canvasRef.current.toDataURL("image/png");
      }

      const response = await fetch(
        `${API_URL}/api/signature-requests/${sigRequestId}/sign?token=${token}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            signature_type: signatureType,
            typed_signature: signatureType === "typed" ? typedSignature : null,
            drawn_signature: drawnSignatureData,
            signer_name: signerName
          })
        }
      );

      if (response.ok) {
        toast.success("Document signed successfully!");
        // Refresh to show success state
        fetchSignatureRequest();
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || "Failed to submit signature");
      }
    } catch (err) {
      toast.error("Failed to submit signature");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7FFE5] flex items-center justify-center p-4">
        <Card className="w-full max-w-md rounded-2xl border-2 border-[#BFFF00]/30">
          <CardContent className="p-8 text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-[#121212]" />
            <p className="text-[#121212]/60">Loading document...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F7FFE5] flex items-center justify-center p-4">
        <Card className="w-full max-w-md rounded-2xl border-2 border-red-200">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-[#121212] mb-2">Error</h2>
            <p className="text-[#121212]/60">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (sigRequest?.status === "signed") {
    return (
      <div className="min-h-screen bg-[#F7FFE5] flex items-center justify-center p-4">
        <Card className="w-full max-w-md rounded-2xl border-2 border-green-200">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-xl font-bold text-[#121212] mb-2">Document Signed</h2>
            <p className="text-[#121212]/60 mb-4">
              This document was signed on {new Date(sigRequest.signed_at).toLocaleDateString()}
            </p>
            <p className="text-sm text-[#121212]/50">
              Signed by: {sigRequest.signature_data?.signer_name}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (sigRequest?.status === "expired") {
    return (
      <div className="min-h-screen bg-[#F7FFE5] flex items-center justify-center p-4">
        <Card className="w-full max-w-md rounded-2xl border-2 border-amber-200">
          <CardContent className="p-8 text-center">
            <Clock className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-[#121212] mb-2">Request Expired</h2>
            <p className="text-[#121212]/60">
              This signature request has expired. Please contact the sender for a new request.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7FFE5] py-8 px-4">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center">
          <div className="w-16 h-16 bg-[#BFFF00] rounded-2xl flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl font-bold text-[#121212]">F</span>
          </div>
          <h1 className="text-2xl font-bold text-[#121212]">Sign Document</h1>
          <p className="text-[#121212]/60 mt-1">
            {sigRequest?.form_type} form from {sigRequest?.sender_name}
          </p>
        </div>

        {/* Document Info */}
        <Card className="rounded-2xl border-2 border-[#BFFF00]/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Document Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between items-center py-2 border-b border-[#121212]/10">
              <span className="text-[#121212]/60">Form Type</span>
              <span className="font-medium">{sigRequest?.form_type}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-[#121212]/10">
              <span className="text-[#121212]/60">Requested By</span>
              <span className="font-medium">{sigRequest?.sender_name}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-[#121212]/10">
              <span className="text-[#121212]/60">Your Name</span>
              <span className="font-medium">{sigRequest?.recipient_name}</span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-[#121212]/60">Expires</span>
              <span className="font-medium">
                {new Date(sigRequest?.expires_at).toLocaleDateString()}
              </span>
            </div>
            
            {sigRequest?.message && (
              <div className="mt-4 p-3 bg-[#F7FFE5] rounded-xl">
                <p className="text-sm text-[#121212]/70 italic">"{sigRequest.message}"</p>
              </div>
            )}

            {sigRequest?.pdf_url && (
              <a 
                href={`${API_URL}${sigRequest.pdf_url}`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 flex items-center justify-center gap-2 text-[#121212] hover:text-[#121212]/70"
              >
                <Download className="w-4 h-4" />
                <span className="text-sm font-medium">View/Download Document</span>
              </a>
            )}
          </CardContent>
        </Card>

        {/* Signature Section */}
        <Card className="rounded-2xl border-2 border-[#BFFF00]/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <PenTool className="w-5 h-5" />
              Your Signature
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Name Input */}
            <div>
              <Label className="text-sm font-medium">Full Name (as it will appear)</Label>
              <Input
                value={signerName}
                onChange={(e) => setSignerName(e.target.value)}
                placeholder="Enter your full name"
                className="mt-1.5 rounded-xl"
              />
            </div>

            {/* Signature Type Tabs */}
            <Tabs value={signatureType} onValueChange={setSignatureType} className="w-full">
              <TabsList className="grid w-full grid-cols-2 bg-[#F7FFE5] rounded-xl p-1">
                <TabsTrigger value="typed" className="rounded-lg data-[state=active]:bg-white">
                  <Type className="w-4 h-4 mr-2" />
                  Type Signature
                </TabsTrigger>
                <TabsTrigger value="drawn" className="rounded-lg data-[state=active]:bg-white">
                  <PenTool className="w-4 h-4 mr-2" />
                  Draw Signature
                </TabsTrigger>
              </TabsList>

              <TabsContent value="typed" className="mt-4">
                <div>
                  <Label className="text-sm font-medium">Type your signature</Label>
                  <Input
                    value={typedSignature}
                    onChange={(e) => setTypedSignature(e.target.value)}
                    placeholder="Type your name as signature"
                    className="mt-1.5 rounded-xl font-signature text-xl italic"
                    style={{ fontFamily: "cursive" }}
                  />
                  {typedSignature && (
                    <div className="mt-3 p-4 border-2 border-dashed border-[#121212]/20 rounded-xl text-center">
                      <p className="text-sm text-[#121212]/50 mb-2">Preview:</p>
                      <p className="text-2xl italic" style={{ fontFamily: "cursive" }}>
                        {typedSignature}
                      </p>
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="drawn" className="mt-4">
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <Label className="text-sm font-medium">Draw your signature below</Label>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearCanvas}
                      className="text-[#121212]/60 hover:text-[#121212]"
                    >
                      Clear
                    </Button>
                  </div>
                  <div className="border-2 border-dashed border-[#121212]/20 rounded-xl overflow-hidden bg-white">
                    <canvas
                      ref={canvasRef}
                      width={500}
                      height={150}
                      className="w-full cursor-crosshair touch-none"
                      onMouseDown={startDrawing}
                      onMouseMove={draw}
                      onMouseUp={stopDrawing}
                      onMouseLeave={stopDrawing}
                      onTouchStart={startDrawing}
                      onTouchMove={draw}
                      onTouchEnd={stopDrawing}
                    />
                  </div>
                  <p className="text-xs text-[#121212]/50 mt-2 text-center">
                    Use your mouse or finger to draw your signature
                  </p>
                </div>
              </TabsContent>
            </Tabs>

            {/* Submit Button */}
            <Button
              onClick={handleSubmit}
              disabled={submitting || !signerName.trim()}
              className="w-full bg-[#121212] hover:bg-[#2a2a2a] rounded-full h-12 text-base"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Signing...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Sign Document
                </>
              )}
            </Button>

            <p className="text-xs text-center text-[#121212]/50">
              By clicking "Sign Document", you agree that your electronic signature is the legal equivalent of your manual signature.
            </p>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center text-sm text-[#121212]/50">
          Powered by <span className="font-semibold">FinAI by Revurge</span>
        </div>
      </div>
    </div>
  );
}
